import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  SpinnerGap, CaretLeft, CaretRight, Plus, Globe, Storefront,
  ShoppingCart, ChatCircleDots, User, Buildings, X, ArrowSquareOut, CaretDown,
  EnvelopeSimple, Phone, Trash, ArrowsLeftRight,
} from '@phosphor-icons/react';
import { PersonLink } from '@/components/PersonLink';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const FLAG_DEFINITIONS = [
  { key: 'comLeads',       label: 'Com Leads',         icon: ChatCircleDots },
  { key: 'comCompras',     label: 'Com compras',       icon: ShoppingCart },
  { key: 'registadasSite', label: 'Registadas no site', icon: Globe },
  { key: 'associadas',     label: 'Associadas',        icon: User },
];

function fmtEur(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '0,00€';
  return `${Number(v).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
}

function diasSemContactoStyle(d) {
  if (d >= 180) return 'bg-rose-50 text-rose-700 border-rose-200';
  if (d >= 90) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (d >= 30) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  return 'bg-emerald-50 text-emerald-700 border-emerald-200';
}

function SortBtn({ label, k, sort, setSort, align }) {
  const active = sort === k;
  return (
    <button type="button" onClick={() => setSort(k)} data-testid={`companies-sort-${k}`}
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-medium transition-colors ${align === 'right' ? 'justify-end' : ''} ${active ? 'text-[#0F1829]' : 'text-[#6B7280] hover:text-[#111827]'}`}>
      {label}
      <CaretDown size={10} weight="bold" className={active ? 'opacity-100' : 'opacity-30'} />
    </button>
  );
}

function SiteBadge({ c }) {
  if (c.RegistadoNoSite) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200" title={c.TipoCliente || ''}>
      <Globe size={10} weight="fill" /> {c.TipoCliente || 'Site'}
    </span>);
  if (c.TemLeadEmpresa && !c.ID_Cliente && c.NrLeads > 0) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200" title="Pipeline B2B sem cliente associado">
      <Storefront size={10} weight="fill" /> Lead B2B
    </span>);
  if (c.TemLeadEmpresa && c.NrLeads > 0) return (
    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
      <Storefront size={10} weight="fill" /> Só Lead
    </span>);
  return null;
}

function SemContactoBadge({ c }) {
  if (c.DiasSemContacto == null) return <span className="text-[10px] text-[#9CA3AF] italic" title="Sem leads nem encomendas">sem contacto</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border ${diasSemContactoStyle(c.DiasSemContacto)}`}
      title={c.UltimoContacto ? `Último contacto: ${c.UltimoContacto}` : ''}>
      {c.DiasSemContacto === 0 ? 'hoje' : `há ${c.DiasSemContacto} ${c.DiasSemContacto === 1 ? 'dia' : 'dias'}`}
    </span>
  );
}

function LinkBadge({ link }) {
  if (!link?.consultor) {
    return <span className="text-[10px] text-[#9CA3AF] italic">—</span>;
  }
  const isMe = link.tipo === 'eu';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${isMe ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-sky-50 text-sky-700 border border-sky-200'}`}>
      <User size={10} weight="fill" />
      {isMe ? 'Eu' : link.consultor.NomeCompleto}
    </span>
  );
}

export default function CompaniesPage() {
  const [companies, setCompanies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [filterDebounced, setFilterDebounced] = useState('');
  const [flags, setFlags] = useState({ comLeads: false, registadasSite: false, comCompras: false, associadas: false });
  const [scope, setScope] = useState('minhas');   // 'minhas' | 'rede'
  const [sort, setSort] = useState('volume');      // 'volume' | 'leads' | 'nome'
  const [consultorId, setConsultorId] = useState('');   // filtro consultor (só rede)
  const [consultores, setConsultores] = useState([]);
  const [todosConsultores, setTodosConsultores] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [detail, setDetail] = useState(null);      // empresa selecionada (para modal)
  const [counts, setCounts] = useState(null);      // contagens por categoria
  const reqRef = useRef(0);                         // guard de sequência de pedidos

  // Debounce do filtro (server-side)
  useEffect(() => {
    const t = setTimeout(() => setFilterDebounced(filter), 250);
    return () => clearTimeout(t);
  }, [filter]);

  // Consultores da rede (para o filtro do separador "rede")
  useEffect(() => {
    api.get('/rede/consultoresArvore')
      .then(r => setConsultores((r.data?.data || []).filter(c => c.ID_Cliente)))
      .catch(() => setConsultores([]));
  }, []);

  // Todos os consultores CS/CT activos (para transferir empresa **associada**
  // a qualquer colega da IHTP, seja ou não da rede do consultor actual).
  useEffect(() => {
    api.get('/rede/consultoresTodos')
      .then(r => setTodosConsultores((r.data?.data || []).filter(c => c.ID_Cliente)))
      .catch(() => setTodosConsultores([]));
  }, []);

  // Ao sair do separador rede, limpa o consultor selecionado
  useEffect(() => { if (scope !== 'rede') setConsultorId(''); }, [scope]);

  const toggleFlag = (k) => setFlags(s => ({ ...s, [k]: !s[k] }));
  const clearFlags = () => setFlags({ comLeads: false, registadasSite: false, comCompras: false, associadas: false });
  const anyFlagActive = Object.values(flags).some(Boolean);

  const fetchPage = useCallback(async (p, flt, fl, sc, so, cons, ps) => {
    setLoading(true);
    const reqId = ++reqRef.current;
    try {
      const r = await api.post('/empresas/list', {
        page: p, page_size: ps, filter: flt, flags: fl, scope: sc, sort: so,
        consultor_id: cons || undefined,
      });
      if (reqId !== reqRef.current) return; // resposta obsoleta — ignora
      setCompanies(r.data?.data || []);
      setTotal(r.data?.total || 0);
      setCounts(r.data?.counts || null);
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPage(page, filterDebounced, flags, scope, sort, consultorId, pageSize); }, [page, filterDebounced, flags, scope, sort, consultorId, pageSize, fetchPage]);
  useEffect(() => { setPage(1); }, [filterDebounced, flags, scope, sort, consultorId, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const changePage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));

  const reload = () => fetchPage(page, filterDebounced, flags, scope, sort, consultorId, pageSize);
  // Empresa "apenas associada" (sem leads, sem compras, sem registo) e com ficha B2B → eliminável.
  const canDelete = (c) => c.ID_Empresa && !c.RegistadoNoSite && !c.NrEncomendas && !c.NrLeads;
  const deleteEmpresa = async (c, opts = {}) => {
    if (!window.confirm(`Eliminar a empresa "${c.NomeEmpresa}"?\nRemove a ficha B2B (não afeta clientes nem encomendas).`)) return;
    try {
      const { data } = await api.post('/consultant/deleteCompany', { id_empresa: c.ID_Empresa });
      if (data?.success) { toast.success(data.message || 'Empresa eliminada.'); if (opts.closeDetail) setDetail(null); reload(); }
      else toast.error(data?.message || 'Não foi possível eliminar.');
    } catch { toast.error('Erro ao eliminar a empresa.'); }
  };
  const [transferCo, setTransferCo] = useState(null);
  const [transferTo, setTransferTo] = useState('');
  const [transferBusy, setTransferBusy] = useState(false);
  const openTransfer = (c) => { setTransferTo(''); setTransferCo(c); };
  const doTransfer = async () => {
    if (!transferTo) { toast.error('Escolha um consultor.'); return; }
    setTransferBusy(true);
    try {
      const { data } = await api.post('/consultant/transferCompany', { id_empresa: transferCo.ID_Empresa, id_consultor: Number(transferTo) });
      if (data?.success) { toast.success(data.message || 'Empresa transferida.'); setTransferCo(null); setDetail(null); reload(); }
      else toast.error(data?.message || 'Não foi possível transferir.');
    } catch { toast.error('Erro ao transferir a empresa.'); }
    finally { setTransferBusy(false); }
  };

  return (
    <div data-testid="companies-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Empresas</h1>
          <p className="text-sm text-[#6B7280] mt-1">{total} {scope === 'minhas' ? 'empresas suas' : 'empresas da sua rede'} {anyFlagActive ? '(filtro aplicado)' : ''}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select data-testid="companies-sort" value={sort} onChange={e => setSort(e.target.value)}
            className="flex-1 min-w-[150px] sm:flex-none px-3 py-2 border border-[#E5E7EB] rounded-md text-sm bg-white focus:ring-2 focus:ring-[#0F1829] outline-none">
            <option value="volume">Ordenar: Volume de compras</option>
            <option value="encomendas">Ordenar: Nº de encomendas</option>
            <option value="leads">Ordenar: Nº de leads</option>
            <option value="inatividade">Ordenar: Há mais tempo sem contacto</option>
            <option value="pai">Ordenar/Agrupar: Pai (downline)</option>
            <option value="nome">Ordenar: Nome</option>
          </select>
          <input data-testid="companies-filter" placeholder="Filtrar por empresa, NIF, email..." value={filter} onChange={e => setFilter(e.target.value)}
            className="flex-1 min-w-[150px] sm:w-[280px] sm:flex-none px-3 py-2 border border-[#E5E7EB] rounded-md text-sm focus:ring-2 focus:ring-[#0F1829] outline-none" />
          <Link to="/leads/nova?tipoLead=BB&from=empresas" data-testid="new-company-btn"
            className="px-4 py-2 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] whitespace-nowrap flex items-center gap-1.5">
            <Plus size={14} /> Nova Empresa B2B
          </Link>
        </div>
      </div>

      {/* Separadores: as minhas empresas vs empresas da rede */}
      <div className="flex flex-wrap items-center gap-3 mb-4 border-b border-[#E5E7EB]" data-testid="companies-scope-tabs">
        {[{ k: 'minhas', label: 'As minhas empresas' }, { k: 'rede', label: 'Empresas da minha rede' }].map(t => (
          <button key={t.k} onClick={() => setScope(t.k)} data-testid={`companies-scope-${t.k}`}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ${scope === t.k ? 'border-[#0F1829] text-[#0F1829]' : 'border-transparent text-[#6B7280] hover:text-[#111827]'}`}>
            {t.label}
          </button>
        ))}
        {scope === 'rede' && (
          <div className="ml-auto flex items-center gap-2 pb-1" data-testid="companies-consultor-filter">
            <User size={13} className="text-[#6B7280]" />
            <select data-testid="companies-consultor-select" value={consultorId} onChange={e => setConsultorId(e.target.value)}
              className="px-3 py-1.5 border border-[#E5E7EB] rounded-md text-sm bg-white focus:ring-2 focus:ring-[#0F1829] outline-none max-w-[280px]">
              <option value="">Todos os consultores da rede</option>
              {consultores.map(c => (
                <option key={c.ID_Cliente} value={c.ID_Cliente}>{c.NomeCompleto} (#{c.ID_Cliente})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Filtro por toggles — agregação OR (mostra empresas que satisfaçam qualquer um dos critérios activos) */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="companies-toggles">
        <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] mr-1">Filtros:</span>
        {FLAG_DEFINITIONS.map(({ key, label, icon: Icon }) => {
          const active = flags[key];
          return (
            <button key={key} onClick={() => toggleFlag(key)} data-testid={`companies-toggle-${key}`}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${active ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'bg-white text-[#374151] border-[#E5E7EB] hover:bg-[#F9FAFB]'}`}>
              <Icon size={12} /> {label}{counts && counts[key] != null ? ` (${counts[key]})` : ''}
            </button>
          );
        })}
        {anyFlagActive && (
          <button onClick={clearFlags} data-testid="companies-toggles-clear"
            className="text-[11px] underline text-[#6B7280] hover:text-[#0F1829] ml-1">
            Limpar
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
            <SpinnerGap size={28} className="animate-spin text-[#0F1829]" />
          </div>
        )}
        {/* Cabeçalho (desktop) */}
        <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_92px_108px_116px_128px_128px] gap-3 px-4 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]" data-testid="companies-header">
          <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Empresa</span>
          <SortBtn label="Leads" k="leads" sort={sort} setSort={setSort} />
          <SortBtn label="Encomendas" k="encomendas" sort={sort} setSort={setSort} />
          <SortBtn label="Valor" k="volume" sort={sort} setSort={setSort} align="right" />
          <SortBtn label="Sem contacto" k="inatividade" sort={sort} setSort={setSort} />
          <span className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280]">Ligado a</span>
        </div>

        {/* Linhas */}
        <div className="divide-y divide-[#F3F4F6]">
          {companies.map((c, i) => {
            const showGroup = sort === 'pai' && (i === 0 || (companies[i - 1].NomePai || '—') !== (c.NomePai || '—'));
            return (
            <Fragment key={c.Key}>
            {showGroup && (
              <div className="px-4 py-2 bg-[#F3F4F6] border-y border-[#E5E7EB] sticky top-0 z-[1]" data-testid={`companies-group-${c.PaiId || 'none'}`}>
                <span className="text-[11px] uppercase tracking-[0.15em] font-semibold text-[#374151] inline-flex items-center gap-1.5">
                  <User size={12} /> Pai: {c.NomePai || 'Sem pai'}
                </span>
              </div>
            )}
            <div data-testid={`company-row-${c.Key}`} onClick={() => setDetail(c)}
              className="px-4 py-3 hover:bg-[#F9FAFB] transition-colors cursor-pointer">
              {/* ---------- DESKTOP ---------- */}
              <div className="hidden lg:grid grid-cols-[minmax(0,1fr)_92px_108px_116px_128px_128px] gap-3 items-center">
                <div className="min-w-0 flex items-start gap-2">
                  <Buildings size={15} className="text-[#6B7280] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium text-[#111827] truncate" title={c.NomeEmpresa}>{c.NomeEmpresa || '—'}</p>
                      <SiteBadge c={c} />
                    </div>
                    <p className="text-[11px] text-[#6B7280] mt-0.5 truncate">
                      {c.NIF && <span className="font-mono">{c.NIF}</span>}
                      {c.NomeResponsavel && <span onClick={(e) => e.stopPropagation()}> · <PersonLink id={c.ID_Cliente} name={c.NomeResponsavel} /></span>}
                      {c.Localidade && <span className="text-[#9CA3AF]"> · {c.Localidade}</span>}
                      {c.NomePai && <span className="text-[#9CA3AF]" data-testid={`company-pai-${c.Key}`}> · Pai: <span className="text-[#6B7280]">{c.NomePai}</span></span>}
                    </p>
                    {(c.Email || c.Telemovel) && (
                      <p className="text-[11px] mt-0.5 truncate flex items-center gap-2.5" onClick={(e) => e.stopPropagation()}>
                        {c.Email && <a href={`mailto:${String(c.Email).split('/')[0].trim()}`} className="inline-flex items-center gap-1 text-[#002480] hover:underline truncate" title={c.Email} data-testid={`company-email-${c.Key}`}><EnvelopeSimple size={11} className="shrink-0" /><span className="truncate max-w-[220px]">{String(c.Email).split('/')[0].trim()}</span></a>}
                        {c.Telemovel && <a href={`tel:${String(c.Telemovel).replace(/\s/g, '')}`} className="inline-flex items-center gap-1 text-[#002480] hover:underline shrink-0" data-testid={`company-phone-${c.Key}`}><Phone size={11} /> {c.Telemovel}</a>}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-xs font-medium text-[#0F1829]">
                  {c.NrLeads > 0 ? <span className="inline-flex items-center gap-1"><ChatCircleDots size={11} /> {c.NrLeads}</span> : <span className="text-[#9CA3AF]">0</span>}
                </div>
                <div className="text-xs font-medium text-[#0F1829]">
                  {c.NrEncomendas > 0 ? <span className="inline-flex items-center gap-1"><ShoppingCart size={11} /> {c.NrEncomendas}</span> : <span className="text-[#9CA3AF]">0</span>}
                </div>
                <div className="text-xs text-right font-mono text-[#111827] tabular-nums">{fmtEur(c.ValorEncomendas)}</div>
                <div><SemContactoBadge c={c} /></div>
                <div className="min-w-0 flex items-center justify-between gap-1">
                  <LinkBadge link={c.LigadoA} />
                  {canDelete(c) && (
                    <span className="flex items-center gap-0.5 shrink-0">
                      <button type="button" onClick={(e) => { e.stopPropagation(); openTransfer(c); }}
                        data-testid={`company-transfer-${c.Key}`} title="Transferir empresa para outro consultor"
                        className="p-1 rounded text-[#9CA3AF] hover:text-[#002480] hover:bg-blue-50 transition-colors">
                        <ArrowsLeftRight size={14} />
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); deleteEmpresa(c); }}
                        data-testid={`company-delete-${c.Key}`} title="Eliminar empresa (só associadas)"
                        className="p-1 rounded text-[#9CA3AF] hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash size={14} />
                      </button>
                    </span>
                  )}
                </div>
              </div>

              {/* ---------- MOBILE ---------- */}
              <div className="lg:hidden">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex items-start gap-2">
                    <Buildings size={15} className="text-[#6B7280] mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#111827] truncate" title={c.NomeEmpresa}>{c.NomeEmpresa || '—'}</p>
                      <p className="text-[11px] text-[#6B7280] mt-0.5 truncate">
                        {c.NIF && <span className="font-mono">{c.NIF}</span>}
                        {c.Localidade && <span className="text-[#9CA3AF]"> · {c.Localidade}</span>}
                        {c.NomePai && <span className="text-[#9CA3AF]"> · Pai: <span className="text-[#6B7280]">{c.NomePai}</span></span>}
                      </p>
                      {(c.Email || c.Telemovel) && (
                        <p className="text-[11px] mt-0.5 truncate flex items-center gap-2.5" onClick={(e) => e.stopPropagation()}>
                          {c.Email && <a href={`mailto:${String(c.Email).split('/')[0].trim()}`} className="inline-flex items-center gap-1 text-[#002480] hover:underline truncate" title={c.Email}><EnvelopeSimple size={11} className="shrink-0" /><span className="truncate max-w-[160px]">{String(c.Email).split('/')[0].trim()}</span></a>}
                          {c.Telemovel && <a href={`tel:${String(c.Telemovel).replace(/\s/g, '')}`} className="inline-flex items-center gap-1 text-[#002480] hover:underline shrink-0"><Phone size={11} /> {c.Telemovel}</a>}
                        </p>
                      )}
                    </div>
                  </div>
                  <SiteBadge c={c} />
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2 pl-7">
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#374151] bg-[#F3F4F6] rounded px-2 py-0.5"><ChatCircleDots size={11} /> {c.NrLeads || 0} leads</span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-[#374151] bg-[#F3F4F6] rounded px-2 py-0.5"><ShoppingCart size={11} /> {c.NrEncomendas || 0}</span>
                  <span className="text-[11px] font-mono font-medium text-[#111827]">{fmtEur(c.ValorEncomendas)}</span>
                  <SemContactoBadge c={c} />
                  <span className="ml-auto"><LinkBadge link={c.LigadoA} /></span>
                  {canDelete(c) && (
                    <>
                      <button type="button" onClick={(e) => { e.stopPropagation(); openTransfer(c); }}
                        data-testid={`company-transfer-m-${c.Key}`} title="Transferir empresa"
                        className="shrink-0 p-1 rounded text-[#9CA3AF] hover:text-[#002480] hover:bg-blue-50">
                        <ArrowsLeftRight size={14} />
                      </button>
                      <button type="button" onClick={(e) => { e.stopPropagation(); deleteEmpresa(c); }}
                        data-testid={`company-delete-m-${c.Key}`} title="Eliminar empresa (só associadas)"
                        className="shrink-0 p-1 rounded text-[#9CA3AF] hover:text-red-600 hover:bg-red-50">
                        <Trash size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            </Fragment>
            );
          })}
        </div>
        {!loading && companies.length === 0 && <p className="text-center py-6 text-sm text-[#6B7280]">Sem empresas para esta pesquisa.</p>}
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-[#E5E7EB] bg-[#F9FAFB]">
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#6B7280]">Registos por página</label>
              <select data-testid="companies-page-size" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                className="px-2 py-1.5 border border-[#E5E7EB] rounded-md text-xs bg-white focus:ring-2 focus:ring-[#0F1829] outline-none">
                {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className="text-xs text-[#6B7280]">
                {total > 0 ? `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} de ${total}` : `${total} registos`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-xs text-[#6B7280]">Página {page} de {totalPages}</p>
              <div className="flex items-center gap-1">
                <button disabled={page <= 1} onClick={() => changePage(page - 1)} data-testid="companies-page-prev"
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                  <CaretLeft size={12} /> Anterior
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button key={p} onClick={() => changePage(p)} data-testid={`companies-page-${p}`}
                      className={`w-8 h-8 text-xs font-medium rounded border transition-colors ${p === page ? 'bg-[#0F1829] text-white border-[#0F1829]' : 'border-[#E5E7EB] text-[#374151] hover:bg-white'}`}>
                      {p}
                    </button>
                  );
                })}
                <button disabled={page >= totalPages} onClick={() => changePage(page + 1)} data-testid="companies-page-next"
                  className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded border border-[#E5E7EB] text-[#374151] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed">
                  Seguinte <CaretRight size={12} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {detail && <CompanyDetailModal company={detail} onClose={() => setDetail(null)}
        canDelete={canDelete(detail)}
        onDelete={() => deleteEmpresa(detail, { closeDetail: true })}
        onTransfer={() => openTransfer(detail)} />}

      {transferCo && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" onClick={() => !transferBusy && setTransferCo(null)} data-testid="transfer-modal">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Transferir empresa</h3>
            <p className="text-sm text-[#6B7280] mt-1">Transferir <b className="text-[#111827]">{transferCo.NomeEmpresa}</b> para outro consultor.</p>
            <label className="block text-[11px] uppercase tracking-[0.15em] font-medium text-[#6B7280] mt-4 mb-1">Consultor de destino</label>
            <select data-testid="transfer-consultor-select" value={transferTo} onChange={e => setTransferTo(e.target.value)}
              className="w-full px-3 py-2 border border-[#E5E7EB] rounded-md text-sm bg-white focus:ring-2 focus:ring-[#0F1829] outline-none">
              <option value="">— Escolher consultor —</option>
              {todosConsultores
                .filter(cs => cs.ID_Cliente !== (transferCo.PaiId ?? transferCo.LigadoA?.consultor?.ID_Cliente))
                .map(cs => <option key={cs.ID_Cliente} value={cs.ID_Cliente}>{cs.NomeCompleto}</option>)}
            </select>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setTransferCo(null)} disabled={transferBusy}
                className="px-4 py-2 text-sm rounded-md border border-[#E5E7EB] text-[#374151] hover:bg-[#F9FAFB]">Cancelar</button>
              <button type="button" onClick={doTransfer} disabled={transferBusy || !transferTo} data-testid="transfer-confirm-btn"
                className="px-4 py-2 text-sm rounded-md bg-[#002480] text-white hover:bg-[#001a5e] disabled:opacity-50">
                {transferBusy ? 'A transferir…' : 'Transferir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyDetailModal({ company, onClose, canDelete, onDelete, onTransfer }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ encomendas: [], leads: [] });

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.post('/empresas/detalhe', {
          id_empresa: company.ID_Empresa, id_cliente: company.ID_Cliente, nif: company.NIF,
        });
        if (alive) setData({ encomendas: r.data?.encomendas || [], leads: r.data?.leads || [] });
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [company]);

  const totalVol = (data.encomendas || [])
    .filter(o => !['AP', 'CC', 'CP', 'PC'].includes(o.ID_EstadoEncomenda))
    .reduce((s, o) => s + Number(o.PrecoFinal || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto" data-testid="company-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#E5E7EB]">
          <div>
            <div className="flex items-center gap-2">
              <Buildings size={18} className="text-[#0F1829]" />
              <h2 className="text-lg font-bold text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>{company.NomeEmpresa || '—'}</h2>
            </div>
            <p className="text-xs text-[#6B7280] mt-1">
              {company.NIF ? `NIF ${company.NIF}` : 'Sem NIF'}
              {company.LigadoA?.consultor && <> · Consultor: <b>{company.LigadoA.tipo === 'eu' ? 'Eu' : company.LigadoA.consultor.NomeCompleto}</b></>}
              {' · '}Volume: <b>{fmtEur(totalVol)}</b>
            </p>
            {(company.NomeResponsavel || company.Email || company.Telemovel) && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2" data-testid="company-detail-contact">
                {company.NomeResponsavel && (
                  <span className="inline-flex items-center gap-1 text-xs text-[#374151]">
                    <User size={13} className="text-[#6B7280]" /> {company.NomeResponsavel}
                  </span>
                )}
                {company.Telemovel && (
                  <a href={`tel:${String(company.Telemovel).replace(/\s/g, '')}`} data-testid="company-detail-phone"
                    className="inline-flex items-center gap-1 text-xs text-[#002480] hover:underline">
                    <Phone size={13} /> {company.Telemovel}
                  </a>
                )}
                {company.Email && (
                  <a href={`mailto:${String(company.Email).split('/')[0].trim()}`} title={company.Email} data-testid="company-detail-email"
                    className="inline-flex items-center gap-1 text-xs text-[#002480] hover:underline">
                    <EnvelopeSimple size={13} /> {String(company.Email).split('/')[0].trim()}
                  </a>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {canDelete && (
              <>
                <button onClick={onTransfer} data-testid="company-detail-transfer" title="Transferir empresa"
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-[#E5E7EB] text-[#002480] hover:bg-blue-50">
                  <ArrowsLeftRight size={14} /> Transferir
                </button>
                <button onClick={onDelete} data-testid="company-detail-delete" title="Eliminar empresa"
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50">
                  <Trash size={14} /> Eliminar
                </button>
              </>
            )}
            <button onClick={onClose} data-testid="company-detail-close" className="text-[#6B7280] hover:text-[#111827] p-1"><X size={18} /></button>
          </div>
        </div>

        <div className="p-5 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8"><SpinnerGap size={26} className="animate-spin text-[#0F1829]" /></div>
          ) : (
            <>
              {/* Leads / Oportunidades (negócios) */}
              <section data-testid="company-detail-leads">
                <h3 className="text-[11px] uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-2 flex items-center gap-1.5"><ChatCircleDots size={13} /> Oportunidades ({data.leads.length})</h3>
                {data.leads.length === 0 ? <p className="text-sm text-[#9CA3AF]">Sem oportunidades (negócios) associadas.</p> : (
                  <div className="divide-y divide-[#F3F4F6] border border-[#E5E7EB] rounded-lg">
                    {data.leads.map(l => (
                      <div key={l.ID_Negocio} className="flex items-center justify-between px-3 py-2" data-testid={`company-lead-${l.ID_Negocio}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#111827] truncate">{l.Referencia || l.NomeContacto || `Lead #${l.ID_Negocio}`}</p>
                          <p className="text-[11px] text-[#6B7280]">{l.Consultor || '—'} {l.DataInicio ? `· ${l.DataInicio}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200">{l.NomeEstadoAtual || '—'}</span>
                          <Link to={`/negocios/${l.ID_Negocio}`} data-testid={`company-lead-link-${l.ID_Negocio}`}
                            className="text-[#0F1829] hover:text-[#002480]" title="Abrir lead"><ArrowSquareOut size={16} /></Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Encomendas */}
              <section data-testid="company-detail-encomendas">
                <h3 className="text-[11px] uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-2 flex items-center gap-1.5"><ShoppingCart size={13} /> Encomendas ({data.encomendas.length})</h3>
                {data.encomendas.length === 0 ? <p className="text-sm text-[#9CA3AF]">Sem encomendas.</p> : (
                  <div className="divide-y divide-[#F3F4F6] border border-[#E5E7EB] rounded-lg">
                    {data.encomendas.map(o => (
                      <div key={o.ID_Encomenda} className="flex items-center justify-between px-3 py-2" data-testid={`company-order-${o.ID_Encomenda}`}>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#111827]">
                            <Link to={`/encomendas/${o.ID_Encomenda}`} onClick={(e) => e.stopPropagation()}
                              data-testid={`company-order-link-${o.ID_Encomenda}`}
                              className="text-[#002480] hover:underline inline-flex items-center gap-1" title="Abrir encomenda">
                              #{o.ID_Encomenda} <ArrowSquareOut size={13} />
                            </Link>
                            <span className="text-[11px] text-[#9CA3AF] font-normal ml-1">{o.Data || ''}</span>
                          </p>
                          <p className="text-[11px] text-[#6B7280] truncate">{o.Cliente || ''}</p>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-[#F3F4F6] text-[#374151] border border-[#E5E7EB]">{o.Estado || o.ID_EstadoEncomenda}</span>
                          <span className="text-xs font-mono tabular-nums text-[#111827]">{fmtEur(o.PrecoFinal)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
